import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { viewModelToModelForAdGroupSetting, bulkModelToViewModelForAdGroupSetting } from './ad-group-setting';
import {
  viewModelToBulkModelForAdGroupSchedule,
  viewModelToBulkModelForAdGroupDeviceTargets,
  viewModelToBulkModelForAdGroupGenderTargets,
  viewModelToBulkModelForAdGroupAgeTargets,
  viewModelToBulkModelForAdGroupLocationTargets,
  viewModelToBulkModelForAdGroupCompanyNameTargets,
  viewModelToBulkModelForAdGroupIndustryTargets,
  viewModelToBulkModelForAdGroupJobFunctionTargets,
} from './adgroup-criterion';
import { isBulkWrappedResponse } from '../shared/bulk';
import { ERROR_ENTITY, getBulkUploadErrorType, modelToViewModelForBulkErrors } from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY, getErrorsPlaceholders } from '../shared/constants';
import { viewModelToBulkModelForDsaTarget } from './dsa-target';


const adGroupEDM = edmCampaign.types.resolve('Model.AdGroup');
const adGroupResource = new adGroupEDM.ResourceIdentifier();

const createOrUpdateAdGroup = (data) => {
  const createOrUpdateCall = data && data.Id ?
    adGroupResource.bulkUpdate.bind(adGroupResource) :
    adGroupResource.bulkCreate.bind(adGroupResource);
  return createOrUpdateCall(data);
};

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  'AdGroup Day Time Criterion Error': ERROR_ENTITY.AdGroupDayTimeCriterion,
  'AdGroup DeviceOS Criterion Error': ERROR_ENTITY.AdGroupDeviceOSCriterion,
  'AdGroup Gender Criterion Error': ERROR_ENTITY.AdGroupGenderCriterion,
  'AdGroup Age Criterion Error': ERROR_ENTITY.AdGroupAgeCriterion,
  'AdGroup Location Criterion Error': ERROR_ENTITY.AdGroupLocation,
  'AdGroup Radius Criterion Error': ERROR_ENTITY.AdGroupRadius,
  'AdGroup Negative Location Criterion Error': ERROR_ENTITY.AdGroupNegativeLocation,
  'AdGroup Location Intent Criterion Error': ERROR_ENTITY.AdGroupLocationIntent,
  'AdGroup Company Name Criterion Error': ERROR_ENTITY.AdGroupCompanyNameCriterion,
  'AdGroup Industry Criterion Error': ERROR_ENTITY.AdGroupIndustryCriterion,
  'AdGroup Job Function Criterion Error': ERROR_ENTITY.AdGroupJobFunctionCriterion,
};

export const saveAdGroup = ({
  adGroup: adGroupViewModel,
  campaign,
  bundleCountries,
  bundleLocationCriterions,
}, {
  permissions,
  scenario,
  i18n,
  activityLogger,
}) => {
  const adGroupModel = viewModelToModelForAdGroupSetting(adGroupViewModel, campaign.Id, i18n, permissions);
  const scheduleModel = adGroupViewModel.adSchedulesBulkUpsert ? viewModelToBulkModelForAdGroupSchedule(adGroupViewModel, campaign) : [];
  const deviceModel = adGroupViewModel.Device ? viewModelToBulkModelForAdGroupDeviceTargets({ adGroupViewModel }) : [];
  const genderModel = adGroupViewModel.Gender ? viewModelToBulkModelForAdGroupGenderTargets({ adGroupViewModel }) : [];
  const ageModel = adGroupViewModel.Age ? viewModelToBulkModelForAdGroupAgeTargets({ adGroupViewModel }) : [];
  const locationTargetsModel = bundleLocationCriterions ? viewModelToBulkModelForAdGroupLocationTargets(bundleLocationCriterions, adGroupViewModel, campaign) : [];
  const companyNameModel = adGroupViewModel.companyNameTargets ? viewModelToBulkModelForAdGroupCompanyNameTargets({ adGroupViewModel }) : [];
  const industryModel = adGroupViewModel.industry ? viewModelToBulkModelForAdGroupIndustryTargets({ adGroupViewModel }) : [];
  const jobFunctionModel = adGroupViewModel.jobTargets ? viewModelToBulkModelForAdGroupJobFunctionTargets({ adGroupViewModel }) : [];
  const dsaTargetsModel = adGroupViewModel.allowEditDynamicAdTarget && adGroupViewModel.DsaTargets ? {
    DsaTargets: viewModelToBulkModelForDsaTarget({
      dsaViewModel: adGroupViewModel.DsaTargets,
      campaignName: campaign.Name,
      adGroupName: adGroupViewModel.Name,
    }),
  } : {};

  const modelToSave = {
    ...adGroupModel,
    AdGroupCriterions: [
      ...scheduleModel,
      ...deviceModel,
      ...genderModel,
      ...ageModel,
      ...locationTargetsModel,
      ...companyNameModel,
      ...industryModel,
      ...jobFunctionModel,
    ],
    ...dsaTargetsModel,
  };

  return createOrUpdateAdGroup(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = {
        campaign,
        adGroup: bulkModelToViewModelForAdGroupSetting({
          adGroupModel: result,
          originaladGroupModel: adGroupViewModel,
          campaignModel: campaign,
          bundleCountries,
          i18n,
        }),
        ...getErrorsPlaceholders(),
      };

      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;

        const serverErrors = modelToViewModelForBulkErrors(
          errors,
          bulkUpsertEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          campaign,
          adGroup: entity ? bulkModelToViewModelForAdGroupSetting({
            adGroupModel: entity,
            originaladGroupModel: adGroupViewModel,
            campaignModel: campaign,
            bundleCountries,
            i18n,
          }) : adGroupViewModel,
          serverErrors,
        });
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign,
        adGroup: adGroupViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      });
    });
};
